/**
 * BM25 over the published corpus, for the browser.
 *
 * A port of the scoring in api/_radar/rankers/affinity.js, which ranks radar
 * topics by how close they sit to work that already exists here. The Gate asks
 * the same question of a visitor's text — "is this something the site already
 * covers?" — so the same arithmetic answers both, and it runs locally with no
 * model and no network.
 *
 * Lexical rather than embeddings on purpose: a few hundred short documents do
 * not justify shipping an embedding model, and a score you can read beats one
 * you have to trust.
 */

export interface CorpusDoc {
    id:    string;
    kind:  'article' | 'lab' | 'project' | 'role';
    title: string;
    text:  string;
    href:  string;
}

export interface Match {
    doc:   CorpusDoc;
    score: number;
    /** How many distinct query terms this document actually contains. */
    matched: number;
    /** How many the query had, so a caller can judge coverage. */
    terms: number;
    /** matched / terms, 0..1. */
    coverage: number;
}

const K1 = 1.5;

/**
 * Length normalisation, deliberately weaker than the usual 0.75.
 *
 * The corpus is lopsided — a role with six bullet points runs 139 tokens
 * against a 32-token median — and at 0.75 a query for "Zalando" ranked a short
 * article that mentions it once above the role itself. Halving b stops length
 * deciding relevance in a corpus this uneven.
 */
const B = 0.4;

/**
 * A title match counts for more than a body match. "Zalando" in a job title is
 * a different claim from "Zalando" in the middle of a paragraph, and BM25 on a
 * single concatenated field cannot tell them apart.
 */
const TITLE_WEIGHT = 2.5;

/**
 * The routing rule, kept beside the scoring it depends on.
 *
 * Score alone is not enough: "how do I replace a bicycle chain" scored 11.6
 * against "Supply Chain Management Portal", because one rare term landed in a
 * title and the title weight amplified it. A single lucky term is not the site
 * covering a subject, so a real match also has to explain most of the question.
 */
export const COVERS_THRESHOLD = 6;
export const COVERS_COVERAGE = 0.5;

export function covers(match: Match | undefined): boolean {
    return Boolean(match && match.score >= COVERS_THRESHOLD && match.coverage >= COVERS_COVERAGE);
}

const STOPWORDS = new Set([
    'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'your', 'you',
    'are', 'was', 'were', 'has', 'have', 'had', 'its', 'his', 'her', 'their',
    'but', 'not', 'all', 'any', 'can', 'will', 'would', 'been', 'more', 'most',
    'than', 'then', 'when', 'what', 'which', 'who', 'how', 'why', 'out', 'over',
    'new', 'now', 'one', 'two', 'use', 'using', 'used', 'get', 'gets', 'make',
    'makes', 'made', 'via', 'per', 'about', 'after', 'before', 'while', 'does',
    'doing', 'should', 'could', 'may', 'might', 'there', 'here', 'they', 'them',
]);

export function tokenize(text: string): string[] {
    return String(text ?? '')
        .toLowerCase()
        .split(/[^a-z0-9+#.]+/)
        .map(t => t.replace(/^\.+|\.+$/g, ''))
        .filter(t => t.length >= 2 && t.length <= 40 && !STOPWORDS.has(t));
}

interface Field {
    freq:   Map<string, number>;
    length: number;
}

interface Prepared {
    doc:   CorpusDoc;
    title: Field;
    body:  Field;
}

function prepareField(text: string): Field {
    const terms = tokenize(text);
    const freq = new Map<string, number>();
    for (const t of terms) freq.set(t, (freq.get(t) ?? 0) + 1);
    return { freq, length: terms.length };
}

export class Bm25 {
    private readonly docs: Prepared[];
    private readonly idf: Map<string, number>;
    private readonly avgTitle: number;
    private readonly avgBody: number;

    constructor(corpus: CorpusDoc[]) {
        this.docs = corpus.map(doc => ({
            doc,
            title: prepareField(doc.title),
            body: prepareField(doc.text),
        }));

        // Document frequency counts a term once per document, not once per field.
        const df = new Map<string, number>();
        for (const d of this.docs) {
            const seen = new Set([...Array.from(d.title.freq.keys()), ...Array.from(d.body.freq.keys())]);
            seen.forEach(term => df.set(term, (df.get(term) ?? 0) + 1));
        }

        const n = this.docs.length;
        this.avgTitle = this.docs.reduce((s, d) => s + d.title.length, 0) / Math.max(1, n);
        this.avgBody = this.docs.reduce((s, d) => s + d.body.length, 0) / Math.max(1, n);

        // Probabilistic IDF, floored at zero so a near-universal term cannot
        // push a score negative.
        this.idf = new Map();
        df.forEach((count, term) => {
            this.idf.set(term, Math.max(0, Math.log(1 + (n - count + 0.5) / (count + 0.5))));
        });
    }

    private fieldScore(terms: string[], field: Field, avg: number): number {
        let score = 0;
        for (const term of terms) {
            const f = field.freq.get(term);
            if (!f) continue;
            const weight = this.idf.get(term) ?? 0;
            score += weight * ((f * (K1 + 1)) / (f + K1 * (1 - B + B * (field.length / Math.max(1, avg)))));
        }
        return score;
    }

    search(query: string, limit = 4): Match[] {
        const terms = Array.from(new Set(tokenize(query)));
        if (terms.length === 0) return [];

        return this.docs
            .map(d => {
                const matched = terms.filter(t => d.title.freq.has(t) || d.body.freq.has(t)).length;
                return {
                    doc: d.doc,
                    score: TITLE_WEIGHT * this.fieldScore(terms, d.title, this.avgTitle)
                         + this.fieldScore(terms, d.body, this.avgBody),
                    matched,
                    terms: terms.length,
                    coverage: matched / terms.length,
                };
            })
            .filter(m => m.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, limit);
    }

    get size(): number { return this.docs.length; }
}

/** Flatten the generated corpus snapshot into searchable documents. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toDocs(raw: any): CorpusDoc[] {
    const docs: CorpusDoc[] = [];
    for (const a of raw.articles ?? []) {
        docs.push({ id: `article:${a.id}`, kind: 'article', title: a.title, href: `#/article/${a.id}`, text: `${a.category} ${a.summary} ${a.excerpt ?? ''}` });
    }
    for (const l of raw.labs ?? []) {
        docs.push({ id: `lab:${l.id}`, kind: 'lab', title: l.title, href: `#/labs/${l.id}`, text: `${l.category} ${l.summary}` });
    }
    for (const p of raw.projects ?? []) {
        docs.push({ id: `project:${p.id}`, kind: 'project', title: p.title, href: '#/about', text: `${p.subtitle} ${p.summary}` });
    }
    for (const r of raw.experience ?? []) {
        docs.push({ id: `role:${r.company}`, kind: 'role', title: `${r.role}, ${r.company}`, href: '#/about', text: (r.points ?? []).join(' ') });
    }
    return docs;
}
